// src/services/CartService.js - FIXED
// Include necessary imports at the top
import { 
  collection, 
  doc, 
  setDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  Timestamp 
} from "firebase/firestore";
import { auth, db } from "../firebase";

const CARTS_COLLECTION = "carts";
const CART_ITEMS_COLLECTION = "cartItems";

// Get user cart or create one if it doesn't exist
export const getUserCart = async (userId = null) => {
  try {
    // Use the authenticated user ID if not provided
    const uid = userId || auth.currentUser?.uid;
    
    if (!uid) {
      throw new Error("User not authenticated");
    }
    
    // Check if user has a cart already
    const q = query(collection(db, CARTS_COLLECTION), where("userId", "==", uid));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      // Return existing cart
      const cartDoc = querySnapshot.docs[0];
      return { id: cartDoc.id, ...cartDoc.data() };
    } else {
      // Create new cart
      const cartRef = await addDoc(collection(db, CARTS_COLLECTION), {
        userId: uid,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      
      const newCart = await getDoc(cartRef);
      return { id: newCart.id, ...newCart.data() };
    }
    
  } catch (error) {
    console.error("Error getting user cart:", error);
    throw error;
  }
};

// Add item to cart
export const addToCart = async (cartId, productId, quantity = 1) => {
  try {
    if (!cartId) {
      throw new Error("Cart ID is required");
    }
    
    if (!productId) {
      throw new Error("Product ID is required");
    }
    
    // Check if the item already exists in the cart
    const q = query(
      collection(db, CART_ITEMS_COLLECTION),
      where("cartId", "==", cartId),
      where("productId", "==", productId)
    );
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      // Update existing item quantity
      const cartItemDoc = querySnapshot.docs[0];
      const currentQuantity = cartItemDoc.data().quantity || 0;
      
      await updateDoc(doc(db, CART_ITEMS_COLLECTION, cartItemDoc.id), {
        quantity: currentQuantity + quantity,
        updatedAt: Timestamp.now()
      });
      
      return { id: cartItemDoc.id, ...cartItemDoc.data(), quantity: currentQuantity + quantity };
      
    } else {
      // Add new item to cart
      const cartItemRef = await addDoc(collection(db, CART_ITEMS_COLLECTION), {
        cartId,
        productId,
        quantity,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      
      const newCartItem = await getDoc(cartItemRef);
      return { id: newCartItem.id, ...newCartItem.data() };
    }
    
  } catch (error) {
    console.error("Error adding to cart:", error);
    throw error;
  }
};

// Update cart item quantity
export const updateCartItemQuantity = async (cartItemId, quantity) => {
  try {
    if (quantity < 1) {
      throw new Error("Quantity must be at least 1");
    }
    
    const cartItemRef = doc(db, CART_ITEMS_COLLECTION, cartItemId);
    await updateDoc(cartItemRef, {
      quantity,
      updatedAt: Timestamp.now()
    });
    
    const updatedCartItem = await getDoc(cartItemRef);
    return { id: updatedCartItem.id, ...updatedCartItem.data() };
    
  } catch (error) {
    console.error("Error updating cart item quantity:", error);
    throw error;
  }
};

// Remove item from cart
export const removeFromCart = async (cartItemId) => {
  try {
    await deleteDoc(doc(db, CART_ITEMS_COLLECTION, cartItemId));
    return true;
    
  } catch (error) {
    console.error("Error removing from cart:", error);
    throw error;
  }
};

// Clear entire cart
export const clearCart = async (cartId) => {
  try {
    if (!cartId) {
      throw new Error("Cart ID is required");
    }
    
    const q = query(collection(db, CART_ITEMS_COLLECTION), where("cartId", "==", cartId));
    const querySnapshot = await getDocs(q);
    
    // Delete all cart items
    const deletePromises = querySnapshot.docs.map(doc => 
      deleteDoc(doc.ref)
    );
    
    await Promise.all(deletePromises);
    return true;
    
  } catch (error) {
    console.error("Error clearing cart:", error);
    throw error;
  }
};

// Get cart items with product details
export const getCartItems = async (cartId) => {
  try {
    if (!cartId) {
      throw new Error("Cart ID is required");
    }
    
    const q = query(collection(db, CART_ITEMS_COLLECTION), where("cartId", "==", cartId));
    const querySnapshot = await getDocs(q);
    
    // Get cart items
    const cartItems = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // If cart is empty, return empty array
    if (cartItems.length === 0) {
      return [];
    }
    
    // Get product details for each cart item
    const cartItemsWithProducts = await Promise.all(
      cartItems.map(async (item) => {
        try {
          const productRef = doc(db, "products", item.productId);
          const productDoc = await getDoc(productRef);
          
          if (productDoc.exists()) {
            return {
              ...item,
              product: {
                id: productDoc.id,
                ...productDoc.data()
              }
            };
          } else {
            // Product doesn't exist anymore
            return {
              ...item,
              product: { id: item.productId, name: "Product not available" }
            };
          }
        } catch (error) {
          console.error(`Error getting product ${item.productId}:`, error);
          return {
            ...item,
            product: { id: item.productId, name: "Error loading product" }
          };
        }
      })
    );
    
    return cartItemsWithProducts;
    
  } catch (error) {
    console.error("Error getting cart items:", error);
    throw error;
  }
};

// Calculate cart totals
export const getCartTotals = async (cartId) => {
  try {
    if (!cartId) {
      throw new Error("Cart ID is required");
    }
    
    const cartItems = await getCartItems(cartId);
    
    let subtotal = 0;
    let totalItems = 0;
    
    cartItems.forEach(item => {
      const price = item.product?.price || 0;
      const quantity = item.quantity || 0;
      
      subtotal += price * quantity;
      totalItems += quantity;
    });
    
    // Fixed shipping cost for now - could be made dynamic
    // const shippingCost = 0;
    const shippingCost = subtotal > 100 ? 0 : 15;
    const total = subtotal + shippingCost;
    
    return {
      cartId, // Make sure cartId is included in the return object
      subtotal,
      shippingCost,
      total,
      totalItems,
      items: cartItems
    };
    
  } catch (error) {
    console.error("Error calculating cart totals:", error);
    throw error;
  }
};

export default {
  getUserCart,
  addToCart,
  updateCartItemQuantity,
  removeFromCart,
  clearCart,
  getCartItems,
  getCartTotals
};